import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, Check, ChevronLeft, ImagePlus, Plus, Save, Send, Trash2, Sparkles, Share2, Upload, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { NetworkPreview } from "@/components/composer/NetworkPreview";
import { SuggestionsPanel } from "@/components/composer/SuggestionsPanel";
import { InsightsCard } from "@/components/common/InsightsCard";
import { ShareReviewDialog } from "@/components/common/ShareReviewDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import api, { humanError, API_BASE } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

const NETWORKS = ["instagram", "linkedin", "facebook", "x"];
const NET_LABEL = { instagram: "Instagram", linkedin: "LinkedIn", facebook: "Facebook", x: "X (Twitter)" };
const CAP_LIMIT = { instagram: 2200, linkedin: 3000, facebook: 5000, x: 280 };

function newVariant(network) {
  return { network, social_account_id: null, caption: "", hashtags: [], cta: "", media_ids: [], warnings: [] };
}

export default function Composer() {
  const { postId } = useParams();
  const nav = useNavigate();
  const { role, currentTenant } = useAuth();
  const isEditor = can(role, "post.write");

  const [title, setTitle] = useState("Untitled post");
  const [variants, setVariants] = useState([newVariant("instagram")]);
  const [status, setStatus] = useState("draft");
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("instagram");
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(postId || null);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const [mediaOpen, setMediaOpen] = useState(false);
  const [library, setLibrary] = useState(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const mediaFileRef = useRef(null);
  const token = localStorage.getItem("hs_token");

  // Every id this post's variants reference, regardless of which library page
  // loaded -- so a thumbnail still renders for media attached before the
  // picker was opened in this session.
  const mediaById = useMemo(() => {
    const map = {};
    for (const m of library || []) map[m.id] = m;
    return map;
  }, [library]);

  async function loadLibrary() {
    try {
      const { data } = await api.get("/media");
      setLibrary(data.items);
    } catch (e) {
      toast.error(humanError(e));
    }
  }

  function toggleMedia(id) {
    setVariants((vs) => vs.map((x) => {
      if (x.network !== tab) return x;
      const has = x.media_ids.includes(id);
      return { ...x, media_ids: has ? x.media_ids.filter((m) => m !== id) : [...x.media_ids, id] };
    }));
  }

  async function onMediaUpload(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setMediaUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/media", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      await loadLibrary();
      toggleMedia(data.id);
    } catch (err) {
      toast.error(humanError(err));
    } finally {
      setMediaUploading(false);
      if (mediaFileRef.current) mediaFileRef.current.value = "";
    }
  }

  useEffect(() => {
    if (!postId) return;
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get(`/posts/${postId}`);
        if (dead) return;
        setTitle(data.post.title);
        setStatus(data.post.status);
        const vs = data.post.variants && data.post.variants.length ? data.post.variants : [newVariant("instagram")];
        setVariants(vs);
        setTab(vs[0].network);
        setEvents(data.events || []);
        setSavedId(postId);
      } catch (e) {
        toast.error(humanError(e));
      }
    })();
    return () => { dead = true; };
  }, [postId]);

  const currentIdx = variants.findIndex(v => v.network === tab);
  const current = currentIdx >= 0 ? variants[currentIdx] : variants[0];

  const updateVariant = useCallback((patch) => {
    setVariants(vs => vs.map((v, i) => i === currentIdx ? { ...v, ...patch } : v));
  }, [currentIdx]);

  const addNetwork = (n) => {
    if (variants.find(v => v.network === n)) return;
    setVariants(vs => [...vs, newVariant(n)]);
    setTab(n);
  };
  const removeNetwork = (n) => {
    setVariants(vs => vs.filter(v => v.network !== n));
    setTab(variants.find(v => v.network !== n)?.network || "instagram");
  };

  async function save(newStatus) {
    setBusy(true);
    try {
      const payload = { title, variants };
      let id = savedId;
      if (id) {
        await api.patch(`/posts/${id}`, payload);
      } else {
        const { data } = await api.post("/posts", payload);
        id = data.id;
        setSavedId(id);
        nav(`/app/composer/${id}`, { replace: true });
      }
      if (newStatus === "submit") {
        const { data } = await api.post(`/posts/${id}/submit`);
        setStatus(data.status);
        toast.success(currentTenant?.approval_required === false ? "Post approved" : "Submitted for review");
      } else {
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function doSchedule() {
    if (!savedId) return toast.error("Save the post first");
    if (!scheduleDate) return toast.error("Pick a date");
    const [hh, mm] = scheduleTime.split(":").map((n) => parseInt(n, 10));
    const dt = new Date(scheduleDate);
    dt.setHours(hh || 9, mm || 0, 0, 0);
    setBusy(true);
    try {
      await api.post(`/posts/${savedId}/schedule`, { run_at: dt.toISOString() });
      toast.success("Scheduled");
      setStatus("scheduled");
      setScheduleOpen(false);
      nav("/app/calendar");
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!savedId) return nav("/app");
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/posts/${savedId}`);
      toast.success("Deleted");
      nav("/app");
    } catch (e) {
      toast.error(humanError(e));
    }
  }

  const editable = ["draft", "changes_requested"].includes(status);
  const canApprove = can(role, "post.approve");
  const canSchedule = can(role, "post.schedule");

  async function applySuggestion(sugg) {
    updateVariant({
      ...(sugg.caption !== undefined ? { caption: sugg.caption } : {}),
      ...(sugg.hashtags !== undefined ? { hashtags: sugg.hashtags } : {}),
      ...(sugg.cta !== undefined ? { cta: sugg.cta } : {}),
    });
    toast.success("Applied");
  }

  async function approvalAction(action, comment = "") {
    if (!savedId) return;
    try {
      await api.post(`/posts/${savedId}/approval`, { action, comment });
      const { data } = await api.get(`/posts/${savedId}`);
      setStatus(data.post.status);
      setEvents(data.events);
      toast.success(action === "approve" ? "Approved" : action === "request_changes" ? "Changes requested" : "Comment added");
    } catch (e) {
      toast.error(humanError(e));
    }
  }

  return (
    <div>
      <button onClick={() => nav(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-[#43535E] hover:text-[#0B2B33]" data-testid="composer-back">
        <ChevronLeft className="h-4 w-4" /> Back
      </button>
      <PageHeader
        title={
          <input
            data-testid="composer-title-input"
            value={title} onChange={(e) => setTitle(e.target.value)} disabled={!editable || !isEditor}
            className="w-full max-w-xl bg-transparent font-display text-2xl font-bold text-[#0B2B33] outline-none md:text-3xl disabled:opacity-70"
          />
        }
        subtitle={<span className="inline-flex items-center gap-2 text-sm">Status: <span className="hs-chip">{status}</span></span>}
        actions={
          <>
            {isEditor && editable ? (
              <button data-testid="composer-save" onClick={() => save()} disabled={busy} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">
                <Save className="mr-2 -mt-0.5 inline h-4 w-4" /> Save
              </button>
            ) : null}
            {isEditor && editable ? (
              <button data-testid="composer-submit" onClick={() => save("submit")} disabled={busy} className="hs-btn-primary text-sm">
                <Send className="mr-2 -mt-0.5 inline h-4 w-4" /> Submit for review
              </button>
            ) : null}
            {canSchedule && (status === "approved" || status === "scheduled" || status === "failed") ? (
              <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
                <DialogTrigger asChild>
                  <button data-testid="composer-schedule" className="hs-btn-primary text-sm">
                    <CalendarClock className="mr-2 -mt-0.5 inline h-4 w-4" /> Schedule
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Schedule post</DialogTitle>
                    <DialogDescription>Pick a date and time to publish across all selected networks.</DialogDescription>
                  </DialogHeader>
                  <div className="flex flex-col gap-4 md:flex-row">
                    <Calendar mode="single" selected={scheduleDate} onSelect={setScheduleDate} data-testid="schedule-calendar" />
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Time</label>
                      <input
                        type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)}
                        data-testid="schedule-time-input"
                        className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm"
                      />
                      <p className="mt-3 text-xs text-[#8A97A0]">Uses your local timezone.</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <button onClick={() => setScheduleOpen(false)} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm">Cancel</button>
                    <button onClick={doSchedule} disabled={busy} data-testid="schedule-confirm" className="hs-btn-primary text-sm">Schedule</button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : null}
            {savedId && (status === "in_review" || status === "approved") ? (
              <button data-testid="composer-share" onClick={() => setShareOpen(true)} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">
                <Share2 className="mr-2 -mt-0.5 inline h-4 w-4" /> Share for review
              </button>
            ) : null}
            {savedId && isEditor ? (
              <button data-testid="composer-delete" onClick={del} className="rounded-full border border-[#E4E9EF] px-3 py-2 text-sm font-semibold text-[#E3252A] hover:bg-[#FFE6E6]" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            ) : null}
          </>
        }
      />

      {savedId ? (
        <ShareReviewDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          scopeKind="post"
          scopeId={savedId}
          scopeLabel={title}
        />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div>
          <div className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="composer-editor">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Networks</span>
              {NETWORKS.map((n) => {
                const on = !!variants.find(v => v.network === n);
                return (
                  <button
                    key={n}
                    disabled={!editable}
                    onClick={() => on ? removeNetwork(n) : addNetwork(n)}
                    data-testid={`toggle-network-${n}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                      on ? "border-[#1F5FD8] bg-[#EEF3FB] text-[#1F5FD8]" : "border-[#E4E9EF] text-[#43535E] hover:bg-[#F6F8FA]"
                    } disabled:opacity-60`}
                  >
                    {NET_LABEL[n]}
                  </button>
                );
              })}
            </div>

            {variants.length === 0 ? (
              <div className="rounded-lg bg-[#F6F8FA] p-6 text-center text-sm text-[#43535E]">Turn on a network to start writing.</div>
            ) : (
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="h-auto flex-wrap justify-start bg-transparent p-0">
                  {variants.map(v => (
                    <TabsTrigger
                      key={v.network} value={v.network}
                      data-testid={`variant-tab-${v.network}`}
                      className="mr-2 rounded-full border border-[#E4E9EF] px-3 py-1.5 text-xs data-[state=active]:border-[#1F5FD8] data-[state=active]:bg-[#EEF3FB] data-[state=active]:text-[#1F5FD8]"
                    >
                      {NET_LABEL[v.network]}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {variants.map(v => (
                  <TabsContent key={v.network} value={v.network} className="mt-4 space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Caption</label>
                      <textarea
                        data-testid={`caption-input-${v.network}`}
                        value={v.caption}
                        disabled={!editable || !isEditor}
                        onChange={(e) => setVariants(vs => vs.map(x => x.network === v.network ? { ...x, caption: e.target.value } : x))}
                        rows={6}
                        className="w-full rounded-lg border border-[#E4E9EF] p-3 text-sm focus:border-[#1F5FD8] focus:outline-none focus:ring-2 focus:ring-[#1F5FD8]/20 disabled:bg-[#F6F8FA]"
                        placeholder={`Write for ${NET_LABEL[v.network]}…`}
                      />
                      <div className="mt-1 text-xs text-[#8A97A0]">{v.caption.length} / {CAP_LIMIT[v.network]}</div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Hashtags (comma separated)</label>
                      <input
                        data-testid={`hashtags-input-${v.network}`}
                        value={v.hashtags.join(", ")}
                        disabled={!editable || !isEditor}
                        onChange={(e) => setVariants(vs => vs.map(x => x.network === v.network ? { ...x, hashtags: e.target.value.split(",").map(s => s.trim().replace(/^#/, "")).filter(Boolean) } : x))}
                        className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm disabled:bg-[#F6F8FA]"
                        placeholder="design, studio, brand"
                      />
                      {v.hashtags.length ? (
                        <div className="mt-2 flex flex-wrap gap-1">{v.hashtags.map(h => <span key={h} className="hs-chip">#{h}</span>)}</div>
                      ) : null}
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Call to action</label>
                      <input
                        data-testid={`cta-input-${v.network}`}
                        value={v.cta}
                        disabled={!editable || !isEditor}
                        onChange={(e) => setVariants(vs => vs.map(x => x.network === v.network ? { ...x, cta: e.target.value } : x))}
                        className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm disabled:bg-[#F6F8FA]"
                        placeholder="Read the case study"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Media</label>
                      <div className="flex flex-wrap items-center gap-2">
                        {v.media_ids.map((id) => (
                          <div key={id} className="group relative h-16 w-16 overflow-hidden rounded-lg border border-[#E4E9EF] bg-[#F6F8FA]">
                            <img src={`${API_BASE}/media/${id}/download?auth=${token}`} alt="" className="h-full w-full object-cover" />
                            {editable && isEditor ? (
                              <button
                                onClick={() => setVariants(vs => vs.map(x => x.network === v.network ? { ...x, media_ids: x.media_ids.filter(m => m !== id) } : x))}
                                className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white opacity-0 group-hover:opacity-100"
                                title="Remove"
                              >
                                <XIcon className="h-3 w-3" />
                              </button>
                            ) : null}
                          </div>
                        ))}
                        {editable && isEditor ? (
                          <button
                            onClick={() => { setTab(v.network); setMediaOpen(true); if (library === null) loadLibrary(); }}
                            data-testid={`add-media-${v.network}`}
                            className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#E4E9EF] text-[#8A97A0] hover:border-[#1F5FD8] hover:text-[#1F5FD8]"
                          >
                            <ImagePlus className="h-5 w-5" />
                            <span className="text-[10px] font-semibold">Add</span>
                          </button>
                        ) : null}
                      </div>
                      {v.network === "instagram" && v.media_ids.length === 0 ? (
                        <p className="mt-1 text-xs text-[#A1510B]">Instagram requires at least one image or video.</p>
                      ) : null}
                    </div>
                    {(v.warnings || []).length ? (
                      <div className="rounded-lg border border-[#FFB4B7] bg-[#FFF5F5] p-3">
                        {v.warnings.map((w, i) => (
                          <div key={i} className="text-xs text-[#B02328]">• {w}</div>
                        ))}
                      </div>
                    ) : null}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>

          <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add media</DialogTitle>
                <DialogDescription>Pick from your Media Library, or upload something new.</DialogDescription>
              </DialogHeader>
              <input ref={mediaFileRef} type="file" accept="image/*,video/*" hidden onChange={onMediaUpload} data-testid="composer-media-file-input" />
              <button
                onClick={() => mediaFileRef.current?.click()}
                disabled={mediaUploading}
                data-testid="composer-media-upload"
                className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[#E4E9EF] py-3 text-sm font-semibold text-[#43535E] hover:border-[#1F5FD8] hover:text-[#1F5FD8]"
              >
                <Upload className="h-4 w-4" /> {mediaUploading ? "Uploading…" : "Upload new file"}
              </button>
              <div className="max-h-80 overflow-y-auto">
                {library === null ? (
                  <div className="p-4 text-center text-sm text-[#8A97A0]">Loading…</div>
                ) : library.length === 0 ? (
                  <div className="p-4 text-center text-sm text-[#8A97A0]">No media uploaded yet.</div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {library.map((m) => {
                      const selected = current?.media_ids.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          onClick={() => toggleMedia(m.id)}
                          data-testid={`media-pick-${m.id}`}
                          className={`relative aspect-square overflow-hidden rounded-lg border-2 ${selected ? "border-[#1F5FD8]" : "border-transparent"}`}
                          title={m.name}
                        >
                          {m.kind === "image" ? (
                            <img src={`${API_BASE}/media/${m.id}/download?auth=${token}`} alt={m.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-[#F6F8FA] text-[#8A97A0]"><ImagePlus className="h-5 w-5" /></div>
                          )}
                          {selected ? (
                            <span className="absolute right-1 top-1 rounded-full bg-[#1F5FD8] p-0.5 text-white"><Check className="h-3 w-3" /></span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <DialogFooter>
                <button onClick={() => setMediaOpen(false)} data-testid="composer-media-done" className="hs-btn-primary text-sm">Done</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {status === "in_review" && canApprove ? (
            <div className="mt-4 rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="approval-actions">
              <div className="mb-3 text-sm font-semibold text-[#0B2B33]">Review</div>
              <ReviewBox onApprove={(c) => approvalAction("approve", c)} onRequestChanges={(c) => approvalAction("request_changes", c)} onComment={(c) => approvalAction("comment", c)} />
            </div>
          ) : null}

          {events.length ? (
            <div className="mt-4 rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="approval-events">
              <div className="mb-3 text-sm font-semibold text-[#0B2B33]">Activity</div>
              <ul className="space-y-2">
                {events.map(ev => (
                  <li key={ev.id} className="text-xs text-[#43535E]">
                    <span className="font-semibold capitalize text-[#0B2B33]">{ev.action.replace("_", " ")}</span> · {new Date(ev.at).toLocaleString()}
                    {ev.comment ? <span className="ml-1">— "{ev.comment}"</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <aside className="space-y-6">
          <div className="rounded-xl border border-[#E4E9EF] bg-white p-3">
            <div className="mb-2 flex items-center justify-between px-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Live preview</div>
              <span className="text-xs text-[#8A97A0]">{NET_LABEL[current?.network]}</span>
            </div>
            {current ? <NetworkPreview network={current.network} variant={current} brand={currentTenant} /> : null}
          </div>
          {isEditor && editable && current ? (
            <>
              <InsightsCard
                network={current.network}
                onApply={(text) => {
                  const hint = text ? `\nStyle note: ${text}` : "";
                  const brief = `Topic: ${title}\nKey points to include: ${current.caption || "(fill in)"}${hint}`;
                  // Just notify — SuggestionsPanel below reads from state via props on next render.
                  toast.success("Insight will be folded into your next Suggest run");
                  updateVariant({ caption: current.caption + (current.caption ? "\n" : "") });
                }}
              />
              <SuggestionsPanel
                network={current.network}
                brief={`Topic: ${title}\nAudience: our customers\nKey points to include: ${current.caption || "(fill in)"}\nGoal: ${current.cta || "engagement"}`}
                onApply={applySuggestion}
              />
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function ReviewBox({ onApprove, onRequestChanges, onComment }) {
  const [comment, setComment] = useState("");
  return (
    <div>
      <textarea
        value={comment} onChange={(e) => setComment(e.target.value)}
        rows={3} placeholder="Add a comment (optional)"
        data-testid="approval-comment"
        className="w-full rounded-lg border border-[#E4E9EF] p-3 text-sm"
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <button data-testid="approve-button" onClick={() => onApprove(comment)} className="hs-btn-primary text-sm">Approve</button>
        <button data-testid="request-changes-button" onClick={() => onRequestChanges(comment)} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">Request changes</button>
        <button data-testid="comment-only-button" onClick={() => { onComment(comment); setComment(""); }} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">Just comment</button>
      </div>
    </div>
  );
}
